import { BusinessResponses } from '@/types'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Copy } from 'lucide-react'
import { useState, useEffect } from 'react'
import { BusinessModel } from '@/http/models/business/Business'
import { toast } from 'sonner'
import { useForm, Controller } from 'react-hook-form'
import { TextInput } from '@/components/atoms/inputs'
import { Spinner } from '@/components/ui/spinner'

interface DataLayerSettingsProps {
    businessProfile: any | null
    onRefresh: () => Promise<void>
}

export const DataLayerSettings = ({ businessProfile, onRefresh }: DataLayerSettingsProps) => {
    const [isSaving, setIsSaving] = useState(false)

    const {
        control,
        handleSubmit,
        getValues,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            google_tag_manager: {
                active: false,
                container_id: '',
                measurement_id: '',
            },
            facebook_pixel: {
                active: false,
                pixel_id: '',
                capi_access_token: '',
                test_event_code: '',
            },
            tiktok_pixel: {
                active: false,
                pixel_id: '',
                events_api_access_token: '',
                test_event_code: '',
            }
        },
    })

    useEffect(() => {
        if (businessProfile?.app_settings) {
            reset({
                google_tag_manager: businessProfile.app_settings.google_tag_manager || { active: false, container_id: '', measurement_id: '' },
                facebook_pixel: businessProfile.app_settings.facebook_pixel || { active: false, pixel_id: '', capi_access_token: '', test_event_code: '' },
                tiktok_pixel: businessProfile.app_settings.tiktok_pixel || { active: false, pixel_id: '', events_api_access_token: '', test_event_code: '' },
            })
        }
    }, [businessProfile, reset])

    const handleCopy = (fieldName: string) => {
        const value = getValues(fieldName as any)
        if (value) {
            navigator.clipboard.writeText(value)
            toast.success('Copied to clipboard')
        } else {
            toast.error('Field is empty')
        }
    }

    const CopyButton = ({ fieldName }: { fieldName: string }) => (
        <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Copy"
            className="text-muted-foreground hover:text-ink"
            onClick={() => handleCopy(fieldName)}
        >
            <Copy className="size-4" />
        </Button>
    )

    const onSubmit = async (data: any) => {
        setIsSaving(true)
        try {
            const response = await BusinessModel.updateBusinessProfile({
                body: {
                    app_settings: {
                        ...businessProfile?.app_settings,
                        ...data,
                    },
                },
            })
            if (response.success) {
                toast.success('Data layer settings updated')
                await onRefresh()
            } else {
                toast.error('Failed to update settings')
            }
        } catch (error) {
            toast.error('An error occurred')
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <div className="space-y-4">
            <Card>
                <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
                    <div className="space-y-1">
                        <CardTitle>Google Tag Manager</CardTitle>
                        <CardDescription>Configure GTM for site-wide tracking and analytics.</CardDescription>
                    </div>
                    <Controller
                        name="google_tag_manager.active"
                        control={control}
                        render={({ field }) => (
                            <Switch
                                aria-label="Enable Google Tag Manager"
                                checked={field.value}
                                onCheckedChange={field.onChange}
                            />
                        )}
                    />
                </CardHeader>
                <CardContent className="space-y-4 border-t border-border-subtle pt-4">
                    <TextInput
                        control={control}
                        name="google_tag_manager.container_id"
                        label="Container ID"
                        placeholder="GTM-XXXXXXX"
                        errors={errors}
                        rightElement={<CopyButton fieldName="google_tag_manager.container_id" />}
                    />
                    <TextInput
                        control={control}
                        name="google_tag_manager.measurement_id"
                        label="GA4 measurement ID"
                        placeholder="G-XXXXXXX"
                        errors={errors}
                        rightElement={<CopyButton fieldName="google_tag_manager.measurement_id" />}
                    />
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
                    <div className="space-y-1">
                        <CardTitle>Meta Pixel</CardTitle>
                        <CardDescription>Track conversions and build custom audiences for Meta Ads.</CardDescription>
                    </div>
                    <Controller
                        name="facebook_pixel.active"
                        control={control}
                        render={({ field }) => (
                            <Switch
                                aria-label="Enable Facebook Pixel"
                                checked={field.value}
                                onCheckedChange={field.onChange}
                            />
                        )}
                    />
                </CardHeader>
                <CardContent className="space-y-4 border-t border-border-subtle pt-4">
                    <TextInput
                        control={control}
                        name="facebook_pixel.pixel_id"
                        label="Pixel ID"
                        placeholder="1234567890..."
                        errors={errors}
                        rightElement={<CopyButton fieldName="facebook_pixel.pixel_id" />}
                    />
                    <TextInput
                        control={control}
                        name="facebook_pixel.capi_access_token"
                        label="Conversions API access token"
                        placeholder="EAA..."
                        errors={errors}
                        multiline
                        rows={4}
                        rightElement={<CopyButton fieldName="facebook_pixel.capi_access_token" />}
                    />
                    <TextInput
                        control={control}
                        name="facebook_pixel.test_event_code"
                        label="Conversions API test event code"
                        placeholder="TEST12345"
                        errors={errors}
                        rightElement={<CopyButton fieldName="facebook_pixel.test_event_code" />}
                    />
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
                    <div className="space-y-1">
                        <CardTitle>TikTok Pixel</CardTitle>
                        <CardDescription>Track conversions and build custom audiences for TikTok Ads.</CardDescription>
                    </div>
                    <Controller
                        name="tiktok_pixel.active"
                        control={control}
                        render={({ field }) => (
                            <Switch
                                aria-label="Enable TikTok Pixel"
                                checked={field.value}
                                onCheckedChange={field.onChange}
                            />
                        )}
                    />
                </CardHeader>
                <CardContent className="space-y-4 border-t border-border-subtle pt-4">
                    <TextInput
                        control={control}
                        name="tiktok_pixel.pixel_id"
                        label="Pixel ID"
                        placeholder="C4A..."
                        errors={errors}
                        rightElement={<CopyButton fieldName="tiktok_pixel.pixel_id" />}
                    />
                    <TextInput
                        control={control}
                        name="tiktok_pixel.events_api_access_token"
                        label="Events API access token"
                        placeholder="..."
                        errors={errors}
                        multiline
                        rows={4}
                        rightElement={<CopyButton fieldName="tiktok_pixel.events_api_access_token" />}
                    />
                    <TextInput
                        control={control}
                        name="tiktok_pixel.test_event_code"
                        label="Test event code"
                        placeholder="TEST12345"
                        errors={errors}
                        rightElement={<CopyButton fieldName="tiktok_pixel.test_event_code" />}
                    />
                </CardContent>
            </Card>

            <div className="flex justify-end">
                <Button onClick={handleSubmit(onSubmit)} disabled={isSaving}>
                    {isSaving && <Spinner />}
                    Save
                </Button>
            </div>
        </div>
    )
}
